import { BaseService } from '@common/http-layer/service/base.service'

import {
  EVENTS_ATTENDEES_PAGE_LIMIT,
  type AcceptEventConnectionResponse,
  type DecideEventConnectionParams,
  type DeclineEventConnectionResponse,
  type EventConnectionQueryParams,
  type EventConnectionStatus,
  type EventParticipantConnectionAction,
  type EventsMyEventConnectionItem,
  type EventsMyEventScheduleItem,
  type EventsMyEventTicketAttendeesApiResponse,
  type EventsMyEventTicketAttendeesParams,
  type EventsMyEventTicketAttendeesResponse,
  type EventsMyEventTicketItem,
  type EventsMyEventsListApiResponse,
  type EventsMyEventsListParams,
  type EventsMyEventsListResponse,
  type EventsManagementAdInteractionParams,
  type EventsManagementAdvertisementsParams,
  type EventsManagementAdvertisementsResponse,
  type EventsManagementMyParticipantResponse,
  type RequestEventConnectionParams,
  type RequestEventConnectionResponse,
  EVENTS_SAVED_SESSIONS_PAGE_LIMIT,
  type EventsManagementAttendeesApiResponse,
  type EventsManagementAttendeesParams,
  type EventsManagementAttendeesResponse,
  type EventsManagementUserParams,
  type EventsManagementUserProfileApiResponse,
  type EventsManagementUserProfileResponse,
  type EventsManagementDetailResponse,
  type EventsManagementEventBookmarksApiResponse,
  type EventsManagementEventBookmarksResponse,
  type EventsManagementExhibitorApiResponse,
  type EventsManagementExhibitorBookmarkParams,
  type EventsManagementExhibitorMembershipsApiResponse,
  type EventsManagementExhibitorMembershipsParams,
  type EventsManagementExhibitorMembershipsResponse,
  type EventsManagementLeadContactsApiResponse,
  type EventsManagementLeadExportFile,
  type EventsManagementLeadExportParams,
  type EventsManagementLeadContactsParams,
  type EventsManagementLeadContactsResponse,
  type EventsManagementExhibitorParams,
  type EventsManagementExhibitorResponse,
  type EventsManagementExhibitorMemberUpdateParams,
  type EventsManagementExhibitorUpdateParams,
  type EventsManagementExhibitorsApiResponse,
  type EventsManagementExhibitorsResponse,
  type EventsManagementSavedScheduleApiResponse,
  type EventsManagementSavedSessionsParams,
  type EventsManagementSavedSessionsResponse,
  type EventsManagementSavedSessionParams,
  type EventsManagementScheduleApiResponse,
  type EventsManagementScheduleResponse,
  type EventsManagementSpeakersResponse,
  type EventsManagementSponsorsResponse,
  type EventsPortalEventsBucket,
  type EventsPortalEventsResponse,
  type EventsPortalListParams,
  type EventsPortalStatePage,
  type EventsManagementProfileSession,
  type EventsManagementSession,
} from '../types/events-management'

/** Profile sessions with an id, carrying the viewer's saved state as `saved` like the schedule does. */
const toProfileSessionList = (sessions: readonly EventsManagementProfileSession[] | null | undefined): EventsManagementSession[] =>
  (Array.isArray(sessions) ? sessions : [])
    .filter(session => Boolean(session?.id))
    .map(({ inMySchedule, ...session }) => (inMySchedule === true ? { ...session, saved: true } : session))

export class EventsService extends BaseService {
  constructor() {
    super({
      baseURL: 'REST_API_URLS',
      basePath: '/clientportal-middleware/events-management',
      headers: { developer_version: 'nuxt-cp-events' },
    })
  }

  /** The portal resolves the viewer and location from the authenticated principal; none are sent. */
  async getPortalEvents(params: EventsPortalListParams): Promise<EventsPortalStatePage> {
    const page = params.page ?? 1
    const limit = params.limit ?? 50
    const { data } = await this.get<EventsPortalEventsResponse>('/internal/portal/events', {
      params: { state: params.state, page, limit },
    })

    const bucket: EventsPortalEventsBucket | undefined = Array.isArray(data?.events)
      ? { events: data.events, pagination: data.pagination }
      : data?.events?.[params.state]
    // The portal cannot render a row without an id or a name.
    const events = (Array.isArray(bucket?.events) ? bucket.events : []).filter(event => Boolean(event?.id && event.eventName))

    return {
      events,
      pagination: {
        page: bucket?.pagination?.page ?? page,
        limit: bucket?.pagination?.limit ?? limit,
        total: bucket?.pagination?.total ?? events.length,
        hasMore: bucket?.pagination?.hasMore === true,
      },
    }
  }

  /** The viewer's my-events lists; the portal resolves the viewer and location from the authenticated principal. */
  private async listMyEvents<TItem>(
    list: 'tickets' | 'schedule' | 'connections',
    params: EventsMyEventsListParams,
    isRenderable: (item: TItem | undefined) => boolean
  ): Promise<EventsMyEventsListResponse<TItem>> {
    const page = params.page ?? 1
    const limit = params.limit ?? 20
    const { data } = await this.get<EventsMyEventsListApiResponse<TItem>>(`/internal/portal/my-events/${list}`, {
      params: { page, limit },
    })
    const items = (Array.isArray(data?.items) ? data.items : []).filter(isRenderable)
    return {
      items,
      pagination: {
        page: data?.pagination?.page ?? page,
        limit: data?.pagination?.limit ?? limit,
        total: data?.pagination?.total ?? items.length,
        hasMore: data?.pagination?.hasMore === true,
      },
    }
  }

  /** Rows without an event id cannot be opened, so they are dropped. */
  getMyEventTickets(params: EventsMyEventsListParams = {}): Promise<EventsMyEventsListResponse<EventsMyEventTicketItem>> {
    return this.listMyEvents<EventsMyEventTicketItem>('tickets', params, item => Boolean(item?.event?.eventId))
  }

  getMyEventSchedule(params: EventsMyEventsListParams = {}): Promise<EventsMyEventsListResponse<EventsMyEventScheduleItem>> {
    return this.listMyEvents<EventsMyEventScheduleItem>('schedule', params, item => Boolean(item?.event?.eventId))
  }

  /** Rows without a connection id cannot be messaged or keyed, so they are dropped. */
  getMyEventConnections(params: EventsMyEventsListParams = {}): Promise<EventsMyEventsListResponse<EventsMyEventConnectionItem>> {
    return this.listMyEvents<EventsMyEventConnectionItem>('connections', params, item => Boolean(item?.connectionId))
  }

  /** The tickets the viewer holds for one event; deleted tickets and rows without an id are dropped. */
  async getMyEventTicketAttendees(params: EventsMyEventTicketAttendeesParams): Promise<EventsMyEventTicketAttendeesResponse> {
    const page = params.page ?? 1
    const limit = params.limit ?? 20
    const { data } = await this.get<EventsMyEventTicketAttendeesApiResponse>(
      `/internal/portal/my-events/${encodeURIComponent(params.eventId)}/tickets`,
      { params: { page, limit } }
    )
    const attendees = (Array.isArray(data?.attendees) ? data.attendees : []).filter(attendee => Boolean(attendee?.id) && !attendee.deleted)
    return {
      attendees,
      meta: { tickets: data?.meta?.tickets ?? {}, orders: data?.meta?.orders ?? {} },
      pagination: {
        page: data?.pagination?.page ?? page,
        limit: data?.pagination?.limit ?? limit,
        total: data?.pagination?.total ?? attendees.length,
        hasMore: data?.pagination?.hasMore === true,
      },
    }
  }

  /** The viewer's participant on one event; whether it can be badged is decided when the badge is built from it. */
  async getMyEventParticipant(eventId: string): Promise<EventsManagementMyParticipantResponse | null> {
    const { data } = await this.get<EventsManagementMyParticipantResponse>(`/internal/portal/my-events/${encodeURIComponent(eventId)}/me`)
    return data ?? null
  }

  async getPortalEvent(eventId: string): Promise<EventsManagementDetailResponse> {
    const { data } = await this.get<EventsManagementDetailResponse>(`/internal/portal/events/${encodeURIComponent(eventId)}`)
    const event = data?.event
    // The page cannot render an event without an id or a name.
    return { event: event?.id && event.name ? event : null }
  }

  async getPublicEventSchedule(eventId: string): Promise<EventsManagementScheduleResponse> {
    const { data } = await this.get<EventsManagementScheduleApiResponse>(`/public/events/${encodeURIComponent(eventId)}/schedule`)
    const sessions = (Array.isArray(data?.sessions) ? data.sessions : []).filter(session => Boolean(session?.id && session.title))
    const timezone = data?.settings?.timezone
    return timezone ? { sessions, timezone } : { sessions }
  }

  async getPublicEventSpeakers(eventId: string): Promise<EventsManagementSpeakersResponse> {
    const { data } = await this.get<EventsManagementSpeakersResponse>(`/public/events/${encodeURIComponent(eventId)}/speakers`)
    const speakers = (Array.isArray(data?.speakers) ? data.speakers : []).filter(speaker => Boolean(speaker?.id && speaker.name))
    return { speakers }
  }

  async getPublicEventAdvertisements(params: EventsManagementAdvertisementsParams): Promise<EventsManagementAdvertisementsResponse> {
    const { data } = await this.get<Partial<EventsManagementAdvertisementsResponse>>(
      `/public/events/${encodeURIComponent(params.eventId)}/advertisements`,
      { params: { placement: params.placement } }
    )
    // The page cannot render an advertisement without an id.
    const advertisements = (Array.isArray(data?.advertisements) ? data.advertisements : []).filter(advertisement =>
      Boolean(advertisement?.id)
    )
    return { advertisements }
  }

  async trackAdvertisementInteraction(params: EventsManagementAdInteractionParams): Promise<void> {
    await this.post(
      `/internal/portal/events/${encodeURIComponent(params.eventId)}/advertisements/${encodeURIComponent(params.advertisementId)}/interactions`,
      { placement: params.placement, type: params.type }
    )
  }

  async getPublicEventSponsors(eventId: string): Promise<EventsManagementSponsorsResponse> {
    const { data } = await this.get<Partial<EventsManagementSponsorsResponse>>(`/public/events/${encodeURIComponent(eventId)}/sponsors`)
    // The page cannot render a tier without an id.
    const sponsorTiers = (Array.isArray(data?.sponsorTiers) ? data.sponsorTiers : []).filter(tier => Boolean(tier?.id))
    return { sponsorTiers }
  }

  async getEventAttendees(eventId: string, params: EventsManagementAttendeesParams = {}): Promise<EventsManagementAttendeesResponse> {
    const page = params.page ?? 1
    const limit = params.limit ?? EVENTS_ATTENDEES_PAGE_LIMIT
    const { data } = await this.get<EventsManagementAttendeesApiResponse>(
      `/internal/portal/my-events/${encodeURIComponent(eventId)}/attendees`,
      { params: { page, limit, ...(params.search ? { search: params.search } : {}) } }
    )
    const items = (Array.isArray(data?.items) ? data.items : []).filter(attendee =>
      Boolean(attendee?.participantId && attendee.displayName)
    )
    return {
      items,
      pagination: {
        total: data?.pagination?.total ?? items.length,
        page: data?.pagination?.page ?? page,
        limit: data?.pagination?.limit ?? limit,
        hasNext: data?.pagination?.hasNext === true,
      },
    }
  }

  /** One person at the event — whichever roles they hold — with the viewer's connection action and the booths they staff. */
  async getEventUser(params: EventsManagementUserParams): Promise<EventsManagementUserProfileResponse> {
    // With a role the id is a participant id; without one it is the portal account id.
    const url = `/internal/portal/events/${encodeURIComponent(params.eventId)}/people/${encodeURIComponent(params.id)}`
    const { data } = params.role
      ? await this.get<EventsManagementUserProfileApiResponse>(url, { params: { role: params.role } })
      : await this.get<EventsManagementUserProfileApiResponse>(url)
    // A participant without a portal account still has a profile, so a person needs an id or a name to be shown.
    // A booth without an id cannot be opened.
    const person = data?.person?.clientPortalUserId || data?.person?.name?.trim() ? data.person : undefined
    const memberOf = (Array.isArray(data?.memberOf) ? data.memberOf : []).filter(booth => Boolean(booth?.id))
    // A request cannot be addressed to a participant without an id.
    const participant = data?.participant?.participantId ? data.participant : undefined
    return {
      ...(person ? { person } : {}),
      ...(participant ? { participant } : {}),
      ...(data?.connection ? { connection: data.connection } : {}),
      memberOf,
      speakingAt: toProfileSessionList(data?.speakingAt),
      attending: toProfileSessionList(data?.attending),
    }
  }

  /** The viewer's connection action for each participant; the portal resolves the viewer from the principal. */
  async getEventConnectionActions(params: EventConnectionQueryParams): Promise<readonly EventParticipantConnectionAction[]> {
    const { data } = await this.post<EventParticipantConnectionAction[]>('/internal/portal/my-events/connections/query', params)
    return Array.isArray(data) ? data : []
  }

  async requestEventConnection(params: RequestEventConnectionParams): Promise<RequestEventConnectionResponse> {
    const { data } = await this.post<RequestEventConnectionResponse>('/internal/portal/my-events/connections', params)
    return data
  }

  async getEventConnectionStatuses(connectionIds: readonly string[]): Promise<readonly EventConnectionStatus[]> {
    const { data } = await this.post<EventConnectionStatus[]>('/internal/portal/my-events/connections/statuses', { connectionIds })
    return Array.isArray(data) ? data : []
  }

  async acceptEventConnection({ connectionId, requestVersion }: DecideEventConnectionParams): Promise<AcceptEventConnectionResponse> {
    const { data } = await this.post<AcceptEventConnectionResponse>(
      `/internal/portal/my-events/connections/${encodeURIComponent(connectionId)}/accept`,
      { requestVersion }
    )
    return data
  }

  async declineEventConnection({ connectionId, requestVersion }: DecideEventConnectionParams): Promise<DeclineEventConnectionResponse> {
    const { data } = await this.post<DeclineEventConnectionResponse>(
      `/internal/portal/my-events/connections/${encodeURIComponent(connectionId)}/decline`,
      { requestVersion }
    )
    return data
  }

  /** The authenticated viewer's saved sessions for one event; only sessions still marked saved are kept. */
  async getSavedEventSessions(
    eventId: string,
    params: EventsManagementSavedSessionsParams = {}
  ): Promise<EventsManagementSavedSessionsResponse> {
    const page = params.page ?? 1
    const limit = params.limit ?? EVENTS_SAVED_SESSIONS_PAGE_LIMIT
    const { data } = await this.get<EventsManagementSavedScheduleApiResponse>('/internal/portal/my-events/schedule', {
      params: { eventId, ...(params.page ? { page } : {}), ...(params.limit ? { limit } : {}) },
    })
    const rows: NonNullable<EventsManagementSavedScheduleApiResponse['items']> = Array.isArray(data?.items) ? data.items : []
    const items = rows.map(row => ({
      event: row.event ?? null,
      sessions: (row.sessions ?? []).filter(session => session.sessionId && session.saved === true),
    }))

    return {
      items,
      pagination: {
        page: data?.pagination?.page ?? page,
        limit: data?.pagination?.limit ?? limit,
        total: data?.pagination?.total ?? items.length,
        hasMore: data?.pagination?.hasMore === true,
      },
    }
  }

  async updateSavedSession(params: EventsManagementSavedSessionParams): Promise<void> {
    await this.put(`/internal/portal/my-events/${encodeURIComponent(params.eventId)}/schedule/${encodeURIComponent(params.sessionId)}`, {
      saved: params.saved,
    })
  }

  /** Every exhibitor at the event in one response; the endpoint does not page. */
  async listExhibitors(eventId: string): Promise<EventsManagementExhibitorsResponse> {
    const { data } = await this.get<EventsManagementExhibitorsApiResponse>(
      `/internal/portal/events/${encodeURIComponent(eventId)}/exhibitors`
    )
    // Rows without an id or a name cannot render or open a profile.
    const exhibitors = (Array.isArray(data?.exhibitors) ? data.exhibitors : []).filter(exhibitor =>
      Boolean(exhibitor?.id && exhibitor.name)
    )
    return {
      exhibitors,
      tiers: Array.isArray(data?.tiers) ? data.tiers : [],
    }
  }

  async getExhibitor(params: EventsManagementExhibitorParams): Promise<EventsManagementExhibitorResponse> {
    const { data } = await this.get<EventsManagementExhibitorApiResponse>(
      `/internal/portal/events/${encodeURIComponent(params.eventId)}/exhibitors/${encodeURIComponent(params.exhibitorId)}`
    )
    const exhibitor = data?.exhibitor
    // The profile reads the team off the exhibitor, so a root-level `members` array is folded into it.
    const rawMembers = exhibitor?.members ?? (Array.isArray(data?.members) ? data.members : undefined)
    // A member without an id or a name cannot render or be connected with.
    const members = rawMembers?.filter(member => Boolean(member?.id && member.name))
    return {
      ...(exhibitor ? { exhibitor: members ? { ...exhibitor, members } : exhibitor } : {}),
      tiers: Array.isArray(data?.tiers) ? data.tiers : [],
    }
  }

  /** Contacts the exhibitor's team collected at the event, newest or oldest connection first. */
  async listExhibitorLeadContacts(params: EventsManagementLeadContactsParams): Promise<EventsManagementLeadContactsResponse> {
    const { data } = await this.get<EventsManagementLeadContactsApiResponse>(
      `/internal/portal/events/${encodeURIComponent(params.eventId)}/exhibitors/${encodeURIComponent(params.exhibitorId)}/contacts`,
      {
        params: {
          page: params.page,
          pageLimit: params.pageLimit,
          sort: 'connectedAt',
          order: params.order,
          ...(params.search ? { search: params.search } : {}),
        },
      }
    )
    // A contact without an id cannot key a row, and a collector with neither a portal account nor a contact id cannot be keyed.
    const contacts = (data?.contacts ?? [])
      .filter(contact => Boolean(contact?.contactId))
      .map(contact => ({
        ...contact,
        connectedWith: (contact.connectedWith ?? []).filter(collector => Boolean(collector?.clientPortalUserId || collector?.contactId)),
      }))
    const page = data?.pagination?.page ?? params.page
    const limit = data?.pagination?.limit ?? params.pageLimit
    const total = data?.pagination?.total ?? contacts.length
    return {
      contacts,
      pagination: { page, limit, total, hasMore: data?.pagination?.hasMore ?? page * limit < total },
    }
  }

  /** Every lead the exhibitor's team collected at the event, as one CSV file. */
  async exportExhibitorLeads(params: EventsManagementLeadExportParams): Promise<EventsManagementLeadExportFile> {
    const { data, headers } = await this.getBlob(
      `/internal/portal/events/${encodeURIComponent(params.eventId)}/exhibitors/${encodeURIComponent(params.exhibitorId)}/leads/export`
    )
    // `attachment; filename="leads.csv"` (or the RFC 5987 `filename*=UTF-8''…` form); absent means the caller names it.
    const disposition = headers['content-disposition'] ?? ''
    const encodedName = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(disposition)?.[1]
    const plainName = /filename="?([^";]+)"?/i.exec(disposition)?.[1]
    const fileName = (encodedName ? decodeURIComponent(encodedName.trim().replace(/^"|"$/g, '')) : plainName?.trim()) || undefined
    return { blob: data, ...(fileName ? { fileName } : {}) }
  }

  /** The exhibitors the viewer belongs to for this event; rows without an exhibitor id are dropped. */
  async listExhibitorMemberships(
    params: EventsManagementExhibitorMembershipsParams
  ): Promise<EventsManagementExhibitorMembershipsResponse> {
    const { data } = await this.get<EventsManagementExhibitorMembershipsApiResponse>(
      `/internal/portal/events/${encodeURIComponent(params.eventId)}/exhibitor-memberships`
    )
    return {
      memberships: (Array.isArray(data?.memberships) ? data.memberships : []).filter(membership => Boolean(membership?.exhibitorId)),
    }
  }

  async updateExhibitor(params: EventsManagementExhibitorUpdateParams): Promise<void> {
    await this.put(
      `/internal/portal/events/${encodeURIComponent(params.eventId)}/exhibitors/${encodeURIComponent(params.exhibitorId)}`,
      params.body
    )
  }

  /** Changes one team member's public visibility or contact sharing; only the fields in `body` are touched. */
  async updateExhibitorMember(params: EventsManagementExhibitorMemberUpdateParams): Promise<void> {
    await this.patch(
      `/internal/portal/events/${encodeURIComponent(params.eventId)}/exhibitors/${encodeURIComponent(params.exhibitorId)}/members/${encodeURIComponent(params.memberId)}`,
      params.body
    )
  }

  /** The exhibitors the viewer bookmarked at one event; rows without an id are dropped. */
  async listEventBookmarks(eventId: string): Promise<EventsManagementEventBookmarksResponse> {
    const { data } = await this.get<EventsManagementEventBookmarksApiResponse>(
      `/internal/portal/events/${encodeURIComponent(eventId)}/bookmarks`
    )
    return {
      event: data?.event ?? null,
      exhibitors: (Array.isArray(data?.exhibitors) ? data.exhibitors : []).filter(exhibitor => Boolean(exhibitor?.id)),
    }
  }

  async updateExhibitorBookmark(params: EventsManagementExhibitorBookmarkParams): Promise<void> {
    const eventPath = `/internal/portal/events/${encodeURIComponent(params.eventId)}/bookmarks`
    if (params.bookmarked) {
      await this.post(eventPath, {
        eventId: params.eventId,
        entityType: 'exhibitor',
        entityId: params.exhibitorId,
        bookmarked: true,
        bookmarkedAt: new Date().toISOString(),
      })
      return
    }
    await this.delete(`${eventPath}/exhibitor/${encodeURIComponent(params.exhibitorId)}`)
  }
}
